import {
  convertFromCurrency,
  convertFromPercents,
  removeMasks
} from "./chunk-VZX3AJYE.js";
import {
  isNotEmptyValue
} from "./chunk-NQ7GJGRO.js";

// src/utils/CompanyDocument.ts
var CompanyDocument = class _CompanyDocument {
  value = "";
  constructor(value) {
    this.value = value ?? "";
  }
  get masked() {
    return this.applyMask(this.value);
  }
  get unmasked() {
    return this.removeMask(this.value);
  }
  get isValid() {
    return this.validate(this.value);
  }
  get isEmpty() {
    return this.hasNoValue(this.value);
  }
  get isCompanyDocumentLike() {
    return this.isCompanyDocumentCheck(this.value);
  }
  validate(document) {
    if (this.hasNoValue(document)) return true;
    const cnpj = this.removeMask(document);
    if (this.checkLength(cnpj)) return false;
    if (this.checkDigits(cnpj)) return false;
    let size = cnpj.length - 2;
    const digits = cnpj.substring(size);
    let numbers = cnpj.substring(0, size);
    let sum = 0;
    let pos = size - 7;
    for (let i = size; i >= 1; i--) {
      sum += numbers.charAt(size - i) * pos--;
      if (pos < 2) pos = 9;
    }
    let result = sum % 11 < 2 ? 0 : 11 - sum % 11;
    if (result !== Number.parseInt(digits.charAt(0))) return false;
    size = size + 1;
    numbers = cnpj.substring(0, size);
    sum = 0;
    pos = size - 7;
    for (let i = size; i >= 1; i--) {
      sum += numbers.charAt(size - i) * pos--;
      if (pos < 2) pos = 9;
    }
    result = sum % 11 < 2 ? 0 : 11 - sum % 11;
    if (result !== Number.parseInt(digits.charAt(1))) return false;
    return true;
  }
  checkDigits(document) {
    const invalid = [
      "00000000000000",
      "11111111111111",
      "22222222222222",
      "33333333333333",
      "44444444444444",
      "55555555555555",
      "66666666666666",
      "77777777777777",
      "88888888888888",
      "99999999999999"
    ];
    return !!invalid.find((data) => data === document);
  }
  checkLength(document) {
    return document.length !== 14;
  }
  removeMask(document) {
    return this.hasNoValue(document) ? "" : document.replace(/\D/g, "");
  }
  applyMask(document) {
    return this.hasNoValue(document) ? "" : document.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      "$1.$2.$3/$4-$5"
    );
  }
  isCompanyDocumentCheck(document) {
    return this.hasNoValue(document) ? false : !this.checkLength(this.removeMask(document));
  }
  hasNoValue(document) {
    return !document || document === "";
  }
  static unmasked(document) {
    return new _CompanyDocument(document).unmasked;
  }
};

// src/utils/PersonalDocument.ts
var PersonalDocument = class {
  value = "";
  constructor(value) {
    this.value = value ?? "";
  }
  get masked() {
    return this.applyMask(this.value);
  }
  get unmasked() {
    return this.removeMask(this.value);
  }
  get isValid() {
    return this.validate(this.value);
  }
  get isEmpty() {
    return this.hasNoValue(this.value);
  }
  get isPersonalDocument() {
    return this.isPersonalDocumentCheck(this.value);
  }
  validate(document) {
    if (this.hasNoValue(document)) return true;
    const cpf = this.removeMask(document);
    let sum = 0;
    let rest = 0;
    if (this.checkLength(cpf)) return false;
    if (this.checkDigits(cpf)) return false;
    for (let i = 1; i <= 9; i++)
      sum = sum + Number.parseInt(cpf.substring(i - 1, i)) * (11 - i);
    rest = sum * 10 % 11;
    if (rest === 10 || rest === 11) rest = 0;
    if (rest !== Number.parseInt(cpf.substring(9, 10))) return false;
    sum = 0;
    for (let i = 1; i <= 10; i++)
      sum = sum + Number.parseInt(cpf.substring(i - 1, i)) * (12 - i);
    rest = sum * 10 % 11;
    if (rest === 10 || rest === 11) rest = 0;
    if (rest !== Number.parseInt(cpf.substring(10, 11))) return false;
    return true;
  }
  removeMask(document) {
    return !document ? "" : document.replace(/\D/g, "");
  }
  applyMask(document) {
    return !document ? "" : document.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  checkLength(document) {
    return document.length !== 11;
  }
  checkDigits(document) {
    const invalid = [
      "00000000000",
      "11111111111",
      "22222222222",
      "33333333333",
      "44444444444",
      "55555555555",
      "66666666666",
      "77777777777",
      "88888888888",
      "99999999999"
    ];
    return invalid.find((data) => data === document);
  }
  hasNoValue(document) {
    return !document || document === "";
  }
  isPersonalDocumentCheck(document) {
    return this.hasNoValue(document) ? false : !this.checkLength(this.removeMask(document));
  }
};

// src/utils/Validations.ts
import { DateTime } from "luxon";
var Validations = class {
  static validateLegalAge(dateString) {
    const regex = /^\d{2}\/\d{2}\/\d{4}$/;
    if (!regex.test(dateString)) {
      return false;
    }
    const birthDate = DateTime.fromFormat(dateString, "dd/MM/yyyy");
    const today = DateTime.now();
    const age = today.diff(birthDate).as("years");
    return age >= 18;
  }
  static validateCompanyDocument(document) {
    return new CompanyDocument(document).isValid;
  }
  static validatePersonalDocument(document) {
    return new PersonalDocument(document).isValid;
  }
  static validatePhoneNumber(phoneNumber) {
    const regex = /^\d{10,11}$/;
    const str = removeMasks(phoneNumber.toString());
    if (str.length < 10 || str.length > 11) {
      return false;
    }
    return regex.test(str);
  }
  static validateUnauthorizedProvider(email) {
    const unauthorizedProviders = ["@scansource.com*"];
    return !unauthorizedProviders.some(
      (provider) => new RegExp(provider).test(email)
    );
  }
  static validateNonNullable(arg) {
    return isNotEmptyValue(arg);
  }
  static validateFloat(value) {
    return typeof value === "string" ? /^[0-9\.]*$/i.test(value) : !isNaN(value);
  }
  static validateMoney(value) {
    if (!value) return false;
    const format = convertFromCurrency(value);
    return this.validateFloat(format);
  }
  static validatePercent(value) {
    if (!value) return false;
    const format = convertFromPercents(value);
    return this.validateFloat(format);
  }
  static validateRange(value, min, max) {
    if (!value || isNaN(value)) return false;
    return min <= value && value <= max;
  }
  static validateMoneyRange(value, min, max) {
    if (!value) return false;
    const format = convertFromCurrency(value);
    return this.validateRange(format, min, max);
  }
  static validatePercentRange(value, min, max) {
    if (!value) return false;
    const format = convertFromPercents(value);
    return this.validateRange(format, min, max);
  }
};

export {
  CompanyDocument,
  PersonalDocument,
  Validations
};
//# sourceMappingURL=chunk-BQC2RKYN.js.map